import useQueryFeaturedPreCanneds from '~/components/sportsbook/lobby/queries/pre-canneds/use-query-featured-pre-canneds';
import { StaleTime } from '~/components/sportsbook/lobby/sportsbook-constants';
import { useMemo } from 'react';
import { EventSortOrder, SubscriptionFilters } from '../../api/sportsbook-api-types';
import useEventSubscription from '~/components/sportsbook/lobby/features/event-subscription/use-event-subscription';
import {
	RecommendedBetDto,
	RecommendedBetItemDto,
	RecommendedBetTypeDto
} from '~/components/sportsbook/lobby/api/sbapi/sbapi-api-model';

const usePreCannedRecommendationsWithEventSubscription = (sportId?: string, leagueId?: string) => {
	// Query preCanneds
	const { data: preCannedItems, isLoading: isPreCannedItemsLoading } = useQueryFeaturedPreCanneds(
		{
			sportId,
			leagueId
		},
		{
			isEnabled: true,
			staleTime: StaleTime.MODERATE
		}
	);

	// Subscribe to data
	const subscriptionFilters = useMemo(() => {
		return {
			eventIds: preCannedItems?.map(preCanned => preCanned.eventId),
			marketIds: preCannedItems?.map(preCanned => preCanned.marketId),
			selectionIds: preCannedItems?.map(preCanned => preCanned.selectionId)
		} as SubscriptionFilters;
	}, [preCannedItems]);
	const { events, isInitialized: isEventsInitialized } = useEventSubscription({
		ref: `pre-canned-carousel-events${sportId || ''}-${leagueId || ''}`,
		filters: subscriptionFilters,
		isEnabled: !!preCannedItems,
		orderBy: EventSortOrder.NONE
	});

	// Stich together preCannedItems with data and convert to RecommendedBetDto
	const preparedRecommendedBets = useMemo(() => {
		if (!preCannedItems || !events) {
			return [];
		}

		return preCannedItems
			.map(preCanned => {
				const event = events.find(e => e.id === preCanned.eventId);
				const market = event?.markets?.find(m => m.id === preCanned.marketId);
				const selection = market?.selections?.find(s => s.id === preCanned.selectionId);
				return {
					odds: selection?.trueOdds || 0,
					type: RecommendedBetTypeDto.SINGLE,
					items: [
						{
							event,
							market,
							selection
						} as RecommendedBetItemDto
					]
				};
			})
			.filter(
				preCanned => preCanned.items[0].event && preCanned.items[0].market && preCanned.items[0].selection
			) as RecommendedBetDto[];
	}, [preCannedItems, events]);

	const isLoading = useMemo(
		() => !isEventsInitialized || isPreCannedItemsLoading,
		[isEventsInitialized, isPreCannedItemsLoading]
	);

	return {
		recommendedBets: preparedRecommendedBets,
		isLoading
	};
};

export default usePreCannedRecommendationsWithEventSubscription;
